import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { DegustacaoResumo, useGlobalState } from "../globalState/GlobalStateProvider";
import { GlobalSaveData } from "../globalState/GlobalSaveData";
import getPromocaoVoucher from "../../service/getPromocaoVoucher";
import postAplicarVoucher from "../../service/postAplicarVoucher";
import getUser from "../../service/getUser";

// pdf.js é pesado; só baixa o chunk quando alguma promoção tiver PDF anexado.
const PdfViewer = lazy(() => import("../PdfViewer"));

// Resumo por permissão das degustações ativas (aplicadas e com saldo), pros filtros de consulta marcarem
// a opção coberta. Vouchers da mesma permissão somam o restante.
const resumirDegustacoes = (lista: any[]): DegustacaoResumo[] => {
  const porPermissao = new Map<string, DegustacaoResumo>();
  lista.filter((v) => v.aplicado && v.identificadorPermissao).forEach((v) => {
    const restante = Math.max(0, v.quantidade - v.quantidadeUtilizada);
    if (restante === 0) return;
    const atual = porPermissao.get(v.identificadorPermissao);
    if (atual) atual.restante += restante;
    else porPermissao.set(v.identificadorPermissao, { permissao: v.identificadorPermissao, campanha: v.campanhaDescricao, restante });
  });
  return Array.from(porPermissao.values());
};

// Indicador de degustação na Topbar: mostra a(s) campanha(s) ativa(s) e a quantidade restante,
// e permite aplicar promoções pendentes. São três modais: "Minha degustação" (compacto, lista as ativas),
// "Promoção" (grande, dá destaque à campanha a aplicar) e "Arquivo expandido" (tela cheia, só a arte da
// campanha). Ao carregar com voucher pendente abre o expandido; ao fechá-lo, cai no modal da promoção.
const TopbarDegustacao = () => {
  const { t } = useTranslation();
  const { state } = useGlobalState();
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [promoVisible, setPromoVisible] = useState(false);
  const [expandVisible, setExpandVisible] = useState(false);
  const [hashInput, setHashInput] = useState("");
  const [aplicando, setAplicando] = useState(false);
  const toast = useRef<Toast>(null);
  const idCliente = state.user?.cliente?.id;

  const carregar = useCallback((autoAbrir: boolean) => {
    if (!idCliente) { setVouchers([]); GlobalSaveData("degustacoes", []); return; }
    getPromocaoVoucher().then((data) => {
      const lista = Array.isArray(data) ? data.filter((v: any) => !v.finalizado) : [];
      setVouchers(lista);
      GlobalSaveData("degustacoes", resumirDegustacoes(lista));
      if (!autoAbrir) return;
      const pendentesLista = lista.filter((v: any) => !v.aplicado);
      if (pendentesLista.some((v: any) => v.arquivoBase64)) setExpandVisible(true);
      else if (pendentesLista.length > 0) setPromoVisible(true);
    });
  }, [idCliente]);

  useEffect(() => { carregar(true); }, [carregar]);

  useEffect(() => {
    const handler = () => carregar(false);
    window.addEventListener('degustacaoAtualizar', handler);
    return () => window.removeEventListener('degustacaoAtualizar', handler);
  }, [carregar]);

  const ativos = vouchers.filter((v) => v.aplicado);
  const pendentes = vouchers.filter((v) => !v.aplicado);
  const restanteTotal = ativos.reduce((acc, v) => acc + Math.max(0, v.quantidade - v.quantidadeUtilizada), 0);

  const copiar = (hash: string) => {
    navigator.clipboard?.writeText(hash);
    toast.current?.show({ severity: 'info', summary: t('copiado'), detail: hash, life: 2000 });
  };

  const aplicar = (voucher: any) => {
    const hash = (hashInput || '').trim() || voucher.hash;
    setAplicando(true);
    postAplicarVoucher(hash).then((msg) => {
      if (msg && msg.toLowerCase().includes('sucesso')) {
        toast.current?.show({ severity: 'success', summary: t('sucesso'), detail: msg, life: 4000 });
        getUser().then((user) => {
          GlobalSaveData("user", user);
          setHashInput('');
          setPromoVisible(false);
          carregar(false);
        });
      } else {
        toast.current?.show({ severity: 'warn', summary: t('atencao'), detail: msg, life: 5000 });
      }
    }).catch((error) => {
      toast.current?.show({ severity: 'error', summary: t('erro'), detail: error?.message || String(error), life: 5000 });
    }).finally(() => setAplicando(false));
  };

  if (!idCliente || vouchers.length === 0) return <Toast ref={toast} />;

  // Badge: consultas restantes das degustações ativas; se só houver promoções pendentes, quantas há pra aplicar.
  const badge = ativos.length > 0 ? restanteTotal : pendentes.length;

  const abrirPromocao = () => { setVisible(false); setPromoVisible(true); };
  const fecharExpandido = () => { setExpandVisible(false); setPromoVisible(true); };

  // Arte da campanha (imagem ou PDF), limitada à altura informada.
  const renderArquivo = (v: any, maxHeight: string) => {
    if (!v.arquivoBase64) return null;
    if ((v.tipoArquivo || '').startsWith('image/')) {
      return <img alt={v.nomeArquivo || v.campanhaDescricao} src={`data:${v.tipoArquivo};base64,${v.arquivoBase64}`} style={{ maxWidth: '100%', maxHeight, display: 'block', margin: '0 auto', border: '1px solid #e0e0e0', borderRadius: '6px' }} />;
    }
    return (
      <Suspense fallback={null}>
        <PdfViewer base64={v.arquivoBase64} maxHeight={maxHeight} />
      </Suspense>
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      <Button className="button_relatorio" icon="pi pi-gift" badge={String(badge)} badgeClassName="p-badge-danger badge_degustacao" rounded text style={{ color: 'white' }}
        tooltip={t('degustacao') as string} tooltipOptions={{ position: 'bottom' }} onClick={() => (ativos.length > 0 ? setVisible(true) : setPromoVisible(true))} />

      {/* Minha degustação: compacto, só o resumo das ativas e um atalho pra promoção pendente. */}
      <Dialog header={t('minhaDegustacao') as string} visible={visible} style={{ width: '640px', maxWidth: '95vw' }} onHide={() => setVisible(false)} dismissableMask>
        {ativos.length > 0 && (
          <div>
            <h3 style={{ marginTop: 0 }}>{t('degustacoesAtivas')}</h3>
            {ativos.map((v) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                <span><i className="pi pi-gift" style={{ color: '#183462', marginRight: '0.5rem' }} />{v.campanhaDescricao}</span>
                <Tag severity="success" value={`${Math.max(0, v.quantidade - v.quantidadeUtilizada)} ${t('consultasRestantes')} (${v.quantidadeUtilizada}/${v.quantidade})`} />
              </div>
            ))}
          </div>
        )}
        {pendentes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginTop: '1rem', padding: '0.75rem 1rem', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px' }}>
            <span><i className="pi pi-gift" style={{ color: '#183462', marginRight: '0.5rem' }} /><strong>{t('degustacaoTitulo')}</strong></span>
            <Button type="button" icon="pi pi-arrow-right" iconPos="right" label={t('verPromocao') as string} style={{ backgroundColor: '#183462' }} onClick={abrirPromocao} />
          </div>
        )}
      </Dialog>

      {/* Promoção: modal grande, centralizado, com a campanha em destaque. */}
      <Dialog header={t('degustacaoTitulo') as string} visible={promoVisible} style={{ width: '960px', maxWidth: '95vw' }} contentStyle={{ maxHeight: '80vh' }} onHide={() => setPromoVisible(false)} dismissableMask>
        {pendentes.map((v) => (
          <div key={v.id} style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ margin: '0 0 0.25rem', color: '#183462' }}>{v.campanhaDescricao}</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>{t('degustacaoDescricao')} <strong>{v.quantidade} {t('consultasGratis')}</strong></p>
            {renderArquivo(v, '55vh')}
            <div style={{ marginTop: '1.25rem', padding: '1rem', background: '#f5f7fa', borderRadius: '6px' }}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>{t('seuVoucher')}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <InputText value={v.hash} readOnly style={{ fontFamily: 'monospace', flex: 1 }} />
                <Button type="button" icon="pi pi-copy" label={t('copiar') as string} outlined onClick={() => copiar(v.hash)} />
              </div>
              <p style={{ margin: '1rem 0 0.5rem', fontWeight: 600 }}>{t('coleVoucher')}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <InputText value={hashInput} onChange={(e) => setHashInput(e.target.value)} placeholder={t('coleVoucherPlaceholder') as string} style={{ fontFamily: 'monospace', flex: 1 }} />
                <Button type="button" icon="pi pi-check" label={t('aplicarVoucher') as string} style={{ backgroundColor: '#183462' }} loading={aplicando} onClick={() => aplicar(v)} />
              </div>
            </div>
          </div>
        ))}
      </Dialog>

      {/* Arquivo expandido: tela cheia só com a arte da campanha; ao fechar, abre o modal da promoção. */}
      <Dialog header={t('degustacaoTitulo') as string} visible={expandVisible} maximized onHide={fecharExpandido}
        className="dialog_degustacao_expandido" maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        contentStyle={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}
        pt={{ content: { onClick: (e) => { if (e.target === e.currentTarget) fecharExpandido(); } } }}>
        {pendentes.filter((v) => v.arquivoBase64).map((v) => (
          <div key={v.id} style={{ width: '100%', maxWidth: '1100px' }}>
            {renderArquivo(v, 'calc(100vh - 160px)')}
          </div>
        ))}
      </Dialog>
    </div>
  );
};

export default TopbarDegustacao;
